import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Landmark,
  Building2,
  Sparkles,
  Cloud,
  Baby,
  Coins,
} from "lucide-react";
import PlaceholderNote from "@/components/content/PlaceholderNote";
import Seo from "@/components/Seo";
import { LLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";

const ContentEyebrow = ({ children }) => (
  <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
    {children}
  </span>
);

const AccentLine = () => (
  <div className="flex items-center gap-3 mb-6" aria-hidden="true">
    <span className="block w-10 h-[3px] rounded-full bg-[#2E9CD6]" />
    <svg width="60" height="10" viewBox="0 0 60 10" className="text-[#6BC0E8]">
      <path
        d="M0,5 C10,0 20,10 30,5 C40,0 50,10 60,5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const InfoCard = ({ title, children, to, note }) => {
  const inner = (
    <>
      <h3 className="font-[Poppins] font-bold text-[#173A5E] text-lg mb-2 flex items-start gap-2">
        <span className="min-w-0">{title}</span>
        {to && <ArrowRight className="w-4 h-4 text-[#2E9CD6] mt-1 shrink-0" />}
      </h3>
      <div className="text-[#3E5871] font-[Inter] text-[15px] leading-relaxed">
        {children}
      </div>
      {note && <div className="mt-3">{note}</div>}
    </>
  );
  const className =
    "block h-full bg-white rounded-2xl border border-[#E4EEF5] p-5 sm:p-6 transition hover:border-[#6BC0E8] hover:shadow-md";
  if (to) {
    return (
      <LLink to={to} className={className}>
        {inner}
      </LLink>
    );
  }
  return <div className={className}>{inner}</div>;
};

const FILTER_KEYS = ["met-kinderen", "bij-regen", "gratis"];
const FILTER_ICONS = { "met-kinderen": Baby, "bij-regen": Cloud, gratis: Coins };

export const ZienEnDoenPage = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const c = pickLang(pageContent.zienEnDoen, lang);
  const home = pickLang(pageContent.home, lang);
  const [activeFilter, setActiveFilter] = useState(null);

  const BEZIENSWAARDIGHEDEN = [
    { key: "woudagemaal", icon: Landmark, to: "/woudagemaal", cta: c.cta },
    { key: "lemstersluis", icon: Building2 },
    { key: "dok", icon: Sparkles, to: "/eten-en-drinken", cta: c.ctaEten },
    { key: "kerk", icon: Building2, note: <PlaceholderNote>{c.notes.kerk}</PlaceholderNote> },
    { key: "vuurtoren", icon: Building2 },
  ];

  const MUSEA = [
    { key: "duikmuseum", address: "Kadijk 15", note: <PlaceholderNote>{c.notes.musea}</PlaceholderNote> },
    { key: "indian", address: "Kadijk 23", note: <PlaceholderNote>{c.notes.musea}</PlaceholderNote> },
    { key: "oudheidskamer", note: <PlaceholderNote>{c.notes.oudheidskamer}</PlaceholderNote> },
  ];

  const ACTIVITEITEN = [
    { key: "bij-regen", icon: Cloud },
    { key: "gratis", icon: Coins },
    { key: "kinderen", filterKey: "met-kinderen", icon: Baby, extraNote: c.notes.kinderen },
  ];

  const filteredActivities = useMemo(() => {
    if (!activeFilter) return ACTIVITEITEN;
    return ACTIVITEITEN.filter(
      (a) => (a.filterKey || a.key) === activeFilter,
    );
  }, [activeFilter, ACTIVITEITEN]);

  return (
    <div data-testid="zien-en-doen-page" className="bg-white">
      <Seo title={c.seo.title} description={c.seo.description} lang={lang} />

      {/* Header */}
      <section className="ll-section-soft py-14 sm:py-20">
        <div className="ll-container max-w-4xl">
          <ContentEyebrow>{c.eyebrow}</ContentEyebrow>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            {c.h1}
          </h1>
          <AccentLine />
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed max-w-2xl">
            {c.intro}
          </p>
        </div>
      </section>

      {/* Bezienswaardigheden */}
      <section className="py-14 sm:py-20">
        <div className="ll-container">
          <div className="max-w-3xl mb-8">
            <ContentEyebrow>{c.bezienswaardigheden.eyebrow}</ContentEyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold">
              {c.bezienswaardigheden.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BEZIENSWAARDIGHEDEN.map((b) => (
              <InfoCard
                key={b.key}
                title={c.titles[b.key]}
                to={b.to}
                note={b.note}
              >
                {c.items[b.key]}
                {b.cta && (
                  <span className="inline-flex items-center gap-1 mt-3 text-[#2E9CD6] font-[Poppins] font-semibold text-sm">
                    {b.cta}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                )}
              </InfoCard>
            ))}
          </div>
        </div>
      </section>

      {/* Musea */}
      <section className="ll-section-soft py-14 sm:py-20">
        <div className="ll-container">
          <div className="max-w-3xl mb-8">
            <ContentEyebrow>{c.musea.eyebrow}</ContentEyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold">{c.musea.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {MUSEA.map((m) => (
              <InfoCard
                key={m.key}
                title={
                  <>
                    {c.titles[m.key]}
                    {m.address && (
                      <span className="block text-sm font-[Inter] font-normal text-[#5A7594] mt-0.5">
                        {m.address}
                      </span>
                    )}
                  </>
                }
                note={m.note}
              >
                {c.items[m.key]}
              </InfoCard>
            ))}
          </div>
        </div>
      </section>

      {/* Activiteiten met filters */}
      <section className="py-14 sm:py-20">
        <div className="ll-container">
          <div className="max-w-3xl mb-6">
            <ContentEyebrow>{c.activiteiten.eyebrow}</ContentEyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              {c.activiteiten.title}
            </h2>
            <p className="text-[#3E5871] font-[Inter] text-base">
              {c.activiteiten.intro}
            </p>
          </div>

          <div
            data-testid="activity-filters"
            className="flex flex-wrap gap-2 mb-8"
          >
            {FILTER_KEYS.map((f) => {
              const Icon = FILTER_ICONS[f];
              const active = activeFilter === f;
              return (
                <button
                  key={f}
                  type="button"
                  data-testid={`filter-${f}`}
                  onClick={() => setActiveFilter(active ? null : f)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-[Poppins] font-semibold transition border ${
                    active
                      ? "bg-[#173A5E] text-white border-[#173A5E]"
                      : "bg-white text-[#173A5E] border-[#E4EEF5] hover:border-[#6BC0E8]"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {c.activiteiten.filters[f]}
                </button>
              );
            })}
            {activeFilter && (
              <button
                type="button"
                onClick={() => setActiveFilter(null)}
                className="text-sm font-[Poppins] font-semibold text-[#5A7594] hover:text-[#173A5E] transition px-2"
              >
                {t("common.toon_alles")}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {filteredActivities.map((a) => {
              const Icon = a.icon;
              const testKey = a.filterKey || a.key;
              return (
                <article
                  key={a.key}
                  data-testid={`activity-${testKey}`}
                  className="bg-white rounded-2xl border border-[#E4EEF5] p-5 sm:p-6"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-[Poppins] font-bold text-[#173A5E] text-lg mb-2">
                    {c.titles[a.key === "kinderen" ? "kinderen" : a.key]}
                  </h3>
                  <div className="text-[#3E5871] font-[Inter] text-[15px] leading-relaxed">
                    {c.items[a.key === "kinderen" ? "kinderen" : a.key]}
                    {a.extraNote && (
                      <>
                        {" "}
                        <PlaceholderNote>{a.extraNote}</PlaceholderNote>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ontdek meer */}
      <section className="ll-section-soft py-12 sm:py-14">
        <div className="ll-container max-w-3xl">
          <p className="text-sm font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-3">
            {t("common.ontdek_meer")}
          </p>
          <div className="flex flex-wrap gap-3">
            <LLink to="/watersport-haven" className="ll-btn-primary">
              {home.categories.items["watersport-haven"].title}
              <ArrowRight className="w-4 h-4" />
            </LLink>
            <LLink to="/eten-en-drinken" className="ll-btn-accent">
              {home.categories.items["eten-drinken"].title}
              <ArrowRight className="w-4 h-4" />
            </LLink>
            <LLink to="/winkelen" className="ll-btn-outline">
              {home.categories.items.winkelen.title}
              <ArrowRight className="w-4 h-4" />
            </LLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZienEnDoenPage;
